import { Autocomplete, Checkbox, FormControlLabel, Grid, Link, TextField } from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormError } from 'app/components/FormError/FormError';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';
import { countries, countryToFlag, CountryType } from 'utils/Countries';

interface Props {
  company: Company;
  editMode?: boolean;
}

const CompanyInformation = ({ company, editMode }: Props) => {
  const { t } = useTranslation();
  const formik = useFormikContext<Company>();

  const readOnlyFields = () => {
    return (
      <div className="section">
        <div className="section__header">
          {t(translations.pages.manageCompanies.companyDetails())}
        </div>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.companyName())}>
              {company.name}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.street())}>
              {company.street}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.zip())}>
              {company.zip}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.city())}>
              {company.city}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <LabeledInput displayAlways label={t(translations.common.country())}>
            {company.country}
          </LabeledInput>
        </Grid>
        <Grid container className="section__row">
          <FormControlLabel
            control={<Checkbox checked={company.sameInvoiceaddress} disabled={true} />}
            label={t(translations.common.useSameAddress()) as string}
          />
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.phone())}>
              {company.phone}
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.fax())}>
              {company.fax}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.homepage())}>
              {/* //TODO possible security issue wegen fehlendem rel="noopener"  */}
              <Link href={company.homepage} target="_blank">
                {company.homepage}
              </Link>
            </LabeledInput>
          </Grid>
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.common.email_short())}>
              <Link href={`mailto:${company.email}`}>{company.email}</Link>
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <FormControlLabel
            control={<Checkbox checked={company.agreedToNewsletter} disabled />}
            label={t(translations.pages.companyDetailPage.agreeToNewsletter())}
          />
        </Grid>
        <Grid container className="section__row">
          <LabeledInput label={t(translations.pages.manageCompanies.registerDate())}>
            {new Date(company.createdAt).toLocaleDateString()}
          </LabeledInput>
        </Grid>
      </div>
    );
  };

  if (!editMode) {
    return readOnlyFields();
  }
  const { errors, values, setFieldValue, validateForm } = formik;

  return (
    <div className="section">
      <div className="section__header">
        {t(translations.pages.manageCompanies.companyDetails())}
      </div>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="name"
            error={errors.name}
            helperText={errors.name}
            required
            label={t(translations.common.companyName())}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="street"
            error={errors.street}
            helperText={errors.street}
            required
            label={t(translations.common.street())}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="zip"
            error={errors.zip}
            helperText={errors.zip}
            required
            label={t(translations.common.zip())}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="city"
            error={errors.city}
            helperText={errors.city}
            required
            label={t(translations.common.city())}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row__single">
        <Grid item xs>
          <FormControlLabel
            control={
              <Autocomplete
                options={countries as CountryType[]}
                autoHighlight
                fullWidth
                value={
                  countries.find(c => c.label === values.country)
                    ? countries.find(c => c.label === values.country)
                    : null
                }
                getOptionLabel={option => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: '0.5em' }}>{countryToFlag(option.code)}</span>
                    {option.label} ({option.code})
                  </li>
                )}
                onChange={async (event, value, reason) => {
                  const promiseList = [
                    setFieldValue('country', value?.label, false),
                    ...(values.sameInvoiceaddress
                      ? [setFieldValue('taxId', '', false), setFieldValue('taxNumber', '', false)]
                      : [])
                  ];
                  await Promise.all(promiseList);
                  await validateForm();
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(translations.common.chooseACountry())}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '6 0 7' },
                      autoComplete: 'new-password'
                    }}
                  />
                )}
              />
            }
            label={''}
            style={{ width: '100%', marginBottom: '0.5em' }}
          />
          {errors.country && <FormError message={errors.country} />}
        </Grid>
      </Grid>
      <Grid container className="section__row__single">
        <Grid item xs>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.sameInvoiceaddress}
                onChange={async e => {
                  await Promise.all([
                    setFieldValue('sameInvoiceaddress', e.target.checked, false),
                    setFieldValue('invoiceName', '', false),
                    setFieldValue('invoiceStreet', '', false),
                    setFieldValue('invoiceZip', '', false),
                    setFieldValue('invoiceCity', '', false),
                    setFieldValue('invoiceCountry', '', false)
                  ]);
                  await validateForm();
                }}
              />
            }
            label={t(translations.common.useSameAddress()) as string}
          />
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <Field as={TextField} name="phone" label={t(translations.common.phone())} />
        </Grid>
        <Grid item xs>
          <Field as={TextField} name="fax" label={t(translations.common.fax())} />
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="homepage"
            error={errors.homepage}
            helperText={errors.homepage}
            required
            label={t(translations.common.homepage())}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="email"
            error={errors.email}
            helperText={errors.email}
            required
            label={t(translations.common.email_short())}
          />
        </Grid>
        <Grid container item className="section__row__single">
          <FormControlLabel
            control={
              <Checkbox
                checked={values.agreedToNewsletter}
                onChange={async e => {
                  setFieldValue('agreedToNewsletter', e.target.checked, false);
                  await validateForm();
                }}
              />
            }
            label={t(translations.pages.companyDetailPage.agreeToNewsletter()) as string}
          />
        </Grid>
      </Grid>
    </div>
  );
};
export default CompanyInformation;
